import { ApplicationElement, html } from '@beunity/lit-components';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';

import '@beunity/lit-components/components/avatar/element.js';
import '@beunity/lit-components/components/dot_menu/element.js';
import '@beunity/lit-components/components/timeago/element.js';
import '../notification_menu/element.js';

import style from './style.lit.css';

/**
 * Notification item with picture, title and info content.
 *
 * @prop notificationId - id of the notification
 * @prop createdAt - Time of notification creation
 * @prop href - Link leading to entire notification content
 * @prop isUser - If true, avatar is rounded
 * @prop isRead - When true, indicates the notification was read
 * @prop isPermanent - If true, indicates permanent notification
 * @prop picture - When present, shows user, group or content image
 *
 * @slot default - Notification content
 * @slot menu - For Notification menu
 */

export default class Notification extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  static properties = {
    notificationId: { type: Number },
    createdAt: { type: Date },
    href: { type: String },
    isUser: { type: Boolean },
    isRead: { type: Boolean },
    isPermanent: { type: Boolean },
    picture: { type: String },
  };

  constructor() {
    super();

    this.icon = 'image';
    this.isRead = false;
    this.isPermanent = false;

    window.addEventListener('notification-change', this);
    window.addEventListener('notification-delete', this);
  }

  willUpdate(changedProps) {
    super.willUpdate(changedProps);

    if (changedProps.has('isRead') && this.menu) {
      this.menu.markAsReadOn = !this.isRead;
      this.menu.markAsUnreadOn = this.isRead;
    }
  }

  /** @internal */
  get menu() {
    return this.querySelector('bu-notification-menu');
  }

  handleEvent(e) {
    if (e.target.resourceId != this.notificationId) return;

    if (e.type === 'notification-change') this.isRead = !this.isRead;
    if (e.type === 'notification-delete') this.remove();
  }

  renderAvatar() {
    return html`
      <ef-avatar
        layout="sm"
        rounded=${ifDefined(this.isUser)}
        icon=${this.icon}
        .picture=${this.picture}>
      </ef-avatar>
    `;
  }

  render() {
    const classes = {
      read: this.isRead,
      permanent: this.isPermanent,
    };

    return html`
      <a href=${this.href} class=${classMap(classes)}>
        ${!this.isRead ? html`<span class="status"></span>` : null}
        ${this.renderAvatar()}

        <div class="info">
          <slot></slot>
          <ef-timeago class="date" .timestamp=${this.createdAt}></ef-timeago>
        </div>
      </a>
      <slot name="menu"></slot>
    `;
  }
}

customElements.get('bu-notification') ||
  customElements.define('bu-notification', Notification);
